"use client";

import { createContext, ReactNode, useCallback, useContext } from "react";
import { Group } from "@microsoft/microsoft-graph-types";

import { SubscriptionContextType, Subscriptions } from "@/types/helpDesk";
import { GraphTenant } from "@/types/microsoft";

import { httpRequest } from "@/lib/http";

import { useAuth } from "@/context/util/auth";
import { useUi } from "@/context/util/ui";

const SubscriptionContext = createContext<SubscriptionContextType>(null!);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { setSnackbar } = useUi();
  const { getAccessToken } = useAuth();

  const getUserSubscriptions = useCallback(
    async (upn: string, tenant: GraphTenant = "employee") => {
      try {
        const token = await getAccessToken();

        if (!token) throw new Error("No access token available");

        const baseUrl = `/account-utilities/api/microsoft/graph/users/memberOf`;
        const params = [
          `?upn=${encodeURIComponent(upn)}`,
          `&tenant=${tenant}`,
          `&$count=true`,
          `&$orderBy=displayName`,
          `&$filter=displayName eq 'Dynamic_RecentStudents'`,
          `or displayName eq 'Dynamic_NoCourseStudents'`,
          `or displayName eq 'O365 - A5 for Faculty'`,
          `or displayName eq 'O365 - K12 Affiliates'`,
        ].join("");

        const url = `${baseUrl}${params}`;

        const headers = { Authorization: `Bearer ${token}` };
        const method = "GET";

        const requestInit: RequestInit = { headers, method };

        const groups = (await httpRequest<Group[]>(url, requestInit)).data;

        if (!groups) throw new Error("Empty http response");

        const a5Groups = [
          "Dynamic_RecentStudents",
          "O365 - A5 for Faculty",
          "O365 - K12 Affiliates",
        ];

        const a5 =
          groups.find((group) =>
            a5Groups.includes(group.displayName ?? "-1")
          ) !== undefined;

        const exchangeGroups = a5Groups.concat(["Dynamic_NoCourseStudents"]);

        const exchange =
          groups.find((group) =>
            exchangeGroups.includes(group.displayName ?? "-1")
          ) !== undefined;

        const subscriptions: Subscriptions = { a5, exchange };

        return subscriptions;
      } catch (error) {
        const message =
          "Failed to get user subscriptions. Please try again later.";
        console.error(message, error);

        setSnackbar((prev) => ({
          ...prev,
          message,
          open: true,
          severity: "error",
        }));

        return { a5: false, exchange: false };
      }
    },
    [getAccessToken]
  );

  return (
    <SubscriptionContext.Provider value={{ getUserSubscriptions }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx)
    throw new Error(
      "useSubscription must be used within a SubscriptionContext"
    );
  return ctx;
}
